import { BankTransaction, TransactionFlag, TranchePart } from '../types/reconciliation';
import { Invoice } from '../types/invoice';

interface MatchOptions {
  /** If true, match against Astrafin receivable instead of grand total */
  useAstrafInAmount?: boolean;
}

type ReconciliationListInvoice = Invoice & {
  amountPayableToAstrafin?: number;
  amountPayableToLender?: number;
  amountPaidByAstrafin?: number;
  amountPaidByLender?: number;
  isAstrafinPaid?: boolean;
  isLenderPaid?: boolean;
  astrafinPaidSource?: string | null;
  lenderPaidSource?: string | null;
  customerName?: string;
};

const PAID_SOURCE_EXCEL = 'ASTRAFIN_EXCEL';

function getAstrafinPayableAmount(inv: ReconciliationListInvoice): number {
  return Math.max(0, inv.amountPayableToAstrafin ?? 0);
}

function getLenderPayableAmount(inv: ReconciliationListInvoice): number {
  return Math.max(0, inv.amountPayableToLender ?? 0);
}

const AMOUNT_TOLERANCE_RS = 100;
const DUE_DATE_DAYS_BEFORE = 4;
const DUE_DATE_DAYS_AFTER = 10;
const DAY_MS = 86400000;
const MAX_COMBO_INVOICES = 5;
const MAX_TRANCHE_CREDITS = 6;

const NAME_NOISE = /\b(M\/?S|MS|MESSRS|PVT|PRIVATE|LTD|LIMITED|LLP|LLC|INC|CORP|COMPANY|CO|INDIA|INDIAN|THE|AND|&)\b/g;
const GENERIC_NAME_TOKENS = new Set([
  'INDIA', 'INDIAN', 'ENTERPRISES', 'ENTERPRISE', 'SERVICES', 'SOLUTIONS',
  'GROUP', 'HOLDINGS', 'GLOBAL', 'INTERNATIONAL', 'TECHNOLOGIES', 'TECHNOLOGY',
]);

function normalise(s: string): string {
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function normaliseName(s: string): string {
  return s.toUpperCase().replace(NAME_NOISE, ' ').replace(/[^A-Z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}

function invoiceCustomerName(inv: Invoice): string {
  const ext = inv as ReconciliationListInvoice;
  return (inv.customer?.name ?? ext.customerName ?? '').trim();
}

function nameTokens(s: string): string[] {
  return normaliseName(s)
    .split(' ')
    .filter(t => t.length >= 3 && !GENERIC_NAME_TOKENS.has(t));
}

/** Fuzzy company match — partial name, abbreviations, case-insensitive compact form */
function nameMatch(a: string, b: string): boolean {
  const sa = a?.trim();
  const sb = b?.trim();
  if (!sa || !sb) return false;

  const na = normalise(sa);
  const nb = normalise(sb);
  if (!na || !nb) return false;

  const shorter = na.length <= nb.length ? na : nb;
  const longer = na.length <= nb.length ? nb : na;
  const probeLen = Math.min(shorter.length, Math.max(6, Math.floor(shorter.length * 0.55)));
  if (probeLen >= 4 && longer.includes(shorter.slice(0, probeLen))) return true;

  const ta = nameTokens(sa);
  const tb = nameTokens(sb);
  if (ta.length === 0 || tb.length === 0) return false;

  const shared = ta.filter(t =>
    tb.some(u => u === t || (t.length >= 4 && u.includes(t)) || (u.length >= 4 && t.includes(u)))
  );
  const distinctive = shared.filter(t => t.length >= 4);
  if (distinctive.length >= 1) return true;
  if (shared.length >= 2) return true;

  const brandA = ta[0];
  const brandB = tb[0];
  if (brandA.length >= 4 && brandB.length >= 4) {
    if (brandA === brandB) return true;
    if (brandA.startsWith(brandB.slice(0, 4)) || brandB.startsWith(brandA.slice(0, 4))) return true;
  }

  return false;
}

/** Calendar date as YYYY-MM-DD — avoids UTC timezone shifting API ISO dates */
function parseCalendarDate(dateStr: string | undefined): string | null {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  const iso = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (iso) return iso[1];
  const dmy = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function calendarToMs(dateStr: string): number {
  return new Date(`${dateStr}T12:00:00`).getTime();
}

function getTxnCalendarDate(txn: BankTransaction): string {
  return parseCalendarDate(txn.valueDate) || parseCalendarDate(txn.transactionDate) || '';
}

/** Bank txn date must fall within invoice due date −4 … +10 days */
function isTxnDateInDueWindow(txn: BankTransaction, dueDate: string): boolean {
  const txnDate = getTxnCalendarDate(txn);
  const due = parseCalendarDate(dueDate);
  if (!txnDate || !due) return false;
  const tMs = calendarToMs(txnDate);
  const dMs = calendarToMs(due);
  return tMs >= dMs - DUE_DATE_DAYS_BEFORE * DAY_MS && tMs <= dMs + DUE_DATE_DAYS_AFTER * DAY_MS;
}

function isAmountMatch(actual: number, expected: number): boolean {
  if (expected <= 0) return false;
  return Math.abs(actual - expected) <= AMOUNT_TOLERANCE_RS;
}

function getMatchAmount(inv: Invoice, useAstrafIn: boolean): number {
  const list = inv as ReconciliationListInvoice;
  return useAstrafIn ? getAstrafinPayableAmount(list) : getLenderPayableAmount(list);
}

function isOutstandingForMatch(inv: Invoice, useAstrafIn: boolean): boolean {
  const list = inv as ReconciliationListInvoice;
  if (useAstrafIn) {
    if (list.isAstrafinPaid) return false;
    if (list.astrafinPaidSource === PAID_SOURCE_EXCEL) return false;
    return getAstrafinPayableAmount(list) > 0;
  }
  if (list.isLenderPaid) return false;
  if (list.lenderPaidSource === PAID_SOURCE_EXCEL) return false;
  return getLenderPayableAmount(list) > 0;
}

function rankByProximity(invoices: Invoice[], txn: BankTransaction): Invoice[] {
  const txnDate = getTxnCalendarDate(txn);
  const txnTime = txnDate ? calendarToMs(txnDate) : 0;
  return [...invoices].sort((a, b) => {
    const da = Math.abs(calendarToMs(parseCalendarDate(a.dueDate) ?? '') - txnTime);
    const db = Math.abs(calendarToMs(parseCalendarDate(b.dueDate) ?? '') - txnTime);
    if (da !== db) return da - db;
    return calendarToMs(parseCalendarDate(a.dueDate) ?? '') - calendarToMs(parseCalendarDate(b.dueDate) ?? '');
  });
}

function groupCreditsByCustomer(credits: BankTransaction[]): BankTransaction[][] {
  const groups: BankTransaction[][] = [];
  for (const credit of credits) {
    const name = credit.counterpartyName?.trim();
    if (!name) continue;
    let placed = false;
    for (const group of groups) {
      if (nameMatch(name, group[0].counterpartyName ?? '')) {
        group.push(credit);
        placed = true;
        break;
      }
    }
    if (!placed) groups.push([credit]);
  }
  return groups;
}

/**
 * Case 1: Same company, multiple rentals — one payment covers 2+ invoices
 * e.g. ₹100 + ₹400 rentals paid as single ₹500 deposit
 */
function findInvoiceCombination(
  invoices: Invoice[],
  target: number,
  useAstrafIn: boolean
): Invoice[] | null {
  const items = invoices
    .map(inv => ({ inv, amt: getMatchAmount(inv, useAstrafIn) }))
    .filter(x => x.amt > 0)
    .sort((a, b) => new Date(a.inv.dueDate).getTime() - new Date(b.inv.dueDate).getTime());

  if (items.length < 2) return null;

  const search = (
    start: number,
    picked: typeof items,
    sum: number
  ): Invoice[] | null => {
    if (picked.length >= 2 && isAmountMatch(target, sum)) {
      return picked.map(p => p.inv);
    }
    if (picked.length >= MAX_COMBO_INVOICES) return null;
    for (let i = start; i < items.length; i++) {
      const nextSum = sum + items[i].amt;
      if (nextSum > target + AMOUNT_TOLERANCE_RS) continue;
      const found = search(i + 1, [...picked, items[i]], nextSum);
      if (found) return found;
    }
    return null;
  };

  return search(0, [], 0);
}

function findFifoAllocation(
  invoices: Invoice[],
  target: number,
  useAstrafIn: boolean
): { picked: Invoice[]; unapplied: number } | null {
  const combo = findInvoiceCombination(invoices, target, useAstrafIn);
  if (combo && combo.length >= 2) {
    const total = combo.reduce((s, inv) => s + getMatchAmount(inv, useAstrafIn), 0);
    return { picked: combo, unapplied: Math.max(0, Math.round(target - total)) };
  }

  const sorted = [...invoices].sort(
    (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
  );
  const picked: Invoice[] = [];
  let remaining = target;
  for (const inv of sorted) {
    if (remaining <= AMOUNT_TOLERANCE_RS) break;
    const amt = getMatchAmount(inv, useAstrafIn);
    if (amt <= 0) continue;
    picked.push(inv);
    remaining -= amt;
  }
  if (picked.length < 2) return null;
  const totalPicked = picked.reduce((s, inv) => s + getMatchAmount(inv, useAstrafIn), 0);
  if (!isAmountMatch(target, totalPicked) && remaining > AMOUNT_TOLERANCE_RS) return null;
  return { picked, unapplied: remaining > AMOUNT_TOLERANCE_RS ? Math.round(remaining) : 0 };
}

function isWithinTolerance(actual: number, expected: number, tolerancePercent: number): boolean {
  if (expected <= 0) return false;
  const diff = Math.abs(actual - expected);
  return diff <= expected * (tolerancePercent / 100);
}

/**
 * Case 2: Single rental paid in multiple bank credits (tranches)
 * e.g. ₹200 + ₹200 + ₹100 = ₹500 invoice
 */
function findTrancheSubset(
  credits: BankTransaction[],
  target: number,
  toleranceAbs: number = AMOUNT_TOLERANCE_RS
): BankTransaction[] | null {
  if (credits.length < 2 || target <= 0) return null;
  const ordered = [...credits].sort(
    (a, b) => calendarToMs(getTxnCalendarDate(a) || '1970-01-01') - calendarToMs(getTxnCalendarDate(b) || '1970-01-01')
  );

  const search = (
    start: number,
    picked: BankTransaction[],
    sum: number
  ): BankTransaction[] | null => {
    if (picked.length >= 2 && Math.abs(sum - target) <= toleranceAbs) return picked;
    if (picked.length >= MAX_TRANCHE_CREDITS) return null;
    for (let i = start; i < ordered.length; i++) {
      const c = ordered[i];
      if (c.depositAmount >= target + toleranceAbs) continue;
      const nextSum = sum + c.depositAmount;
      if (nextSum > target + toleranceAbs) continue;
      const found = search(i + 1, [...picked, c], nextSum);
      if (found) return found;
    }
    return null;
  };

  return search(0, [], 0);
}

export function runAutoMatch(
  transactions: BankTransaction[],
  invoices: Invoice[],
  options?: MatchOptions
): BankTransaction[] {
  const useAstrafIn = options?.useAstrafInAmount ?? false;

  const outstandingInvoices = invoices.filter(inv =>
    isOutstandingForMatch(inv, useAstrafIn)
  );

  const existingUTRs = new Set<string>();
  const existingPayments: { invoiceId: string; amount: number; date: string; customer: string }[] = [];
  invoices.forEach(inv => {
    inv.payments.forEach(p => {
      if (p.reference) existingUTRs.add(p.reference.toUpperCase());
      existingPayments.push({
        invoiceId: inv.invoiceId || '',
        amount: p.amount,
        date: p.date,
        customer: invoiceCustomerName(inv),
      });
    });
  });

  const usedInvoiceIds = new Set<string>();

  // Pre-pass: tranche detection (multiple bank credits → one invoice)
  const trancheAssignment = new Map<string, { invoiceId: string; part: TranchePart; confidence: number }>();
  {
    const credits = transactions.filter(t => t.transactionType === 'credit' && t.counterpartyName);
    const creditGroups = groupCreditsByCustomer(credits);

    for (const customerCredits of creditGroups) {
      if (customerCredits.length < 2) continue;
      const sampleName = customerCredits[0].counterpartyName!;
      const customerInvoices = outstandingInvoices.filter(inv =>
        !usedInvoiceIds.has(inv.invoiceId || '') && nameMatch(sampleName, invoiceCustomerName(inv))
      );
      const orderedInvs = [...customerInvoices].sort(
        (a, b) => getMatchAmount(a, useAstrafIn) - getMatchAmount(b, useAstrafIn)
      );
      for (const inv of orderedInvs) {
        if (usedInvoiceIds.has(inv.invoiceId || '')) continue;
        const target = getMatchAmount(inv, useAstrafIn);
        if (target <= 0) continue;
        const eligible = customerCredits.filter(t =>
          !trancheAssignment.has(t.id) && isTxnDateInDueWindow(t, inv.dueDate)
        );
        const subset = findTrancheSubset(eligible, target);
        if (subset && subset.length >= 2) {
          const groupId = `TR-${inv.invoiceId || ''}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          const sortedSubset = [...subset].sort(
            (a, b) => calendarToMs(getTxnCalendarDate(a) || '') - calendarToMs(getTxnCalendarDate(b) || '')
          );
          const siblingTxnIds = sortedSubset.map(s => s.id);
          let running = 0;
          sortedSubset.forEach((s, idx) => {
            running += s.depositAmount;
            trancheAssignment.set(s.id, {
              invoiceId: inv.invoiceId || '',
              confidence: 80,
              part: {
                groupId,
                index: idx + 1,
                total: sortedSubset.length,
                runningSum: Math.round(running),
                invoiceAmount: Math.round(target),
                siblingTxnIds,
              },
            });
          });
          usedInvoiceIds.add(inv.invoiceId || '');
        }
      }
    }
  }

  return transactions.map(txn => {
    if (txn.transactionType === 'debit') return txn;

    const flags: TransactionFlag[] = [];
    const flagMessages: string[] = [];
    const amount = txn.depositAmount;
    const counterparty = txn.counterpartyName || '';

    if (txn.utrNumber && existingUTRs.has(txn.utrNumber.toUpperCase())) {
      flags.push('duplicate');
      flagMessages.push(`UTR ${txn.utrNumber} already recorded`);
      return { ...txn, matchStatus: 'matched' as const, matchConfidence: 100, matchedInvoiceIds: [], flags, flagMessages };
    }

    if (counterparty) {
      const txnDate = getTxnCalendarDate(txn);
      const dupePayment = existingPayments.find(p =>
        Math.abs(p.amount - amount) < 1 &&
        nameMatch(counterparty, p.customer) &&
        txnDate &&
        parseCalendarDate(p.date) &&
        Math.abs(calendarToMs(txnDate) - calendarToMs(parseCalendarDate(p.date)!)) < 2 * DAY_MS
      );
      if (dupePayment) {
        flags.push('duplicate');
        flagMessages.push(`Possible duplicate — ₹${amount} from ${counterparty} already paid on ${dupePayment.date}`);
      }
    }

    const trAssign = trancheAssignment.get(txn.id);
    if (trAssign) {
      flags.push('partial_tranche');
      flagMessages.push(
        `Tranche ${trAssign.part.index} of ${trAssign.part.total} — running ₹${trAssign.part.runningSum.toLocaleString('en-IN')} of ₹${trAssign.part.invoiceAmount.toLocaleString('en-IN')} for ${trAssign.invoiceId}`
      );
      return {
        ...txn,
        matchStatus: 'suggested' as const,
        matchConfidence: trAssign.confidence,
        matchedInvoiceIds: [trAssign.invoiceId],
        tranchePart: trAssign.part,
        flags,
        flagMessages,
      };
    }

    const available = outstandingInvoices.filter(inv => !usedInvoiceIds.has(inv.invoiceId || ''));

    // 1. Exact: amount ±₹100 + customer name + date window
    if (counterparty) {
      const candidates = rankByProximity(
        available.filter(inv => {
          const matchAmt = getMatchAmount(inv, useAstrafIn);
          return matchAmt > 0
            && isAmountMatch(amount, matchAmt)
            && nameMatch(counterparty, invoiceCustomerName(inv))
            && isTxnDateInDueWindow(txn, inv.dueDate);
        }),
        txn
      );

      if (candidates.length > 0) {
        const best = candidates[0];
        usedInvoiceIds.add(best.invoiceId || '');
        return {
          ...txn, matchStatus: 'suggested' as const, matchConfidence: 90,
          matchedInvoiceIds: [best.invoiceId || ''], flags, flagMessages,
        };
      }
    }

    // 2. Clubbed: one payment → 2+ rentals (e.g. ₹100 + ₹400 = ₹500)
    if (counterparty) {
      const customerInvoices = available.filter(inv =>
        nameMatch(counterparty, invoiceCustomerName(inv)) && isTxnDateInDueWindow(txn, inv.dueDate)
      );
      if (customerInvoices.length >= 2) {
        const alloc = findFifoAllocation(customerInvoices, amount, useAstrafIn);
        if (alloc && alloc.picked.length >= 2) {
          alloc.picked.forEach(inv => usedInvoiceIds.add(inv.invoiceId || ''));
          if (alloc.unapplied > 0) {
            flagMessages.push(`₹${alloc.unapplied} left unapplied after multi-invoice allocation — hold as customer advance.`);
          }
          return {
            ...txn, matchStatus: 'suggested' as const, matchConfidence: 85,
            matchedInvoiceIds: alloc.picked.map(i => i.invoiceId || ''),
            unappliedAmount: alloc.unapplied || undefined,
            flags, flagMessages,
          };
        }
      }
    }

    // 3. TDS tolerance — customer name required, no amount-only anonymous match
    if (counterparty) {
      const toleranceCandidates = rankByProximity(
        available.filter(inv => {
          const matchAmt = getMatchAmount(inv, useAstrafIn);
          return matchAmt > 0
            && isWithinTolerance(amount, matchAmt, 10)
            && !isAmountMatch(amount, matchAmt)
            && nameMatch(counterparty, invoiceCustomerName(inv))
            && isTxnDateInDueWindow(txn, inv.dueDate);
        }),
        txn
      );
      if (toleranceCandidates.length > 0) {
        const best = toleranceCandidates[0];
        const expected = getMatchAmount(best, useAstrafIn);
        const diff = Math.round(amount - expected);
        const tds2 = Math.round(best.baseRounded * 0.02);
        const tds10 = Math.round(best.baseRounded * 0.10);
        flags.push('tds_mismatch');
        if (Math.abs(Math.abs(diff) - tds2) < 2) {
          flagMessages.push(`Customer deducted 2% TDS (₹${tds2}) — received ₹${Math.round(amount)} vs expected ₹${Math.round(expected)}.`);
        } else if (Math.abs(Math.abs(diff) - tds10) < 5) {
          flagMessages.push(`Customer deducted 10% TDS (₹${tds10}) — received ₹${Math.round(amount)} vs expected ₹${Math.round(expected)}.`);
        } else {
          flagMessages.push(`Amount differs by ₹${Math.abs(diff)} — possible TDS (expected ₹${Math.round(expected)}, received ₹${Math.round(amount)})`);
        }
        usedInvoiceIds.add(best.invoiceId || '');
        return {
          ...txn, matchStatus: 'suggested' as const, matchConfidence: 65,
          matchedInvoiceIds: [best.invoiceId || ''], flags, flagMessages,
        };
      }
    }

    // 4. Partial payment — single credit < invoice (e.g. ₹200 of ₹500); tranches handled in pre-pass
    if (counterparty) {
      const partialCandidates = rankByProximity(
        available.filter(inv => {
          const matchAmt = getMatchAmount(inv, useAstrafIn);
          return matchAmt > 0
            && amount > 0
            && amount < matchAmt - AMOUNT_TOLERANCE_RS
            && nameMatch(counterparty, invoiceCustomerName(inv))
            && isTxnDateInDueWindow(txn, inv.dueDate);
        }),
        txn
      );
      if (partialCandidates.length > 0) {
        const best = partialCandidates[0];
        const expected = getMatchAmount(best, useAstrafIn);
        flags.push('partial');
        flagMessages.push(`Partial payment — received ₹${Math.round(amount)} of ₹${Math.round(expected)}`);
        usedInvoiceIds.add(best.invoiceId || '');
        return {
          ...txn, matchStatus: 'suggested' as const, matchConfidence: 55,
          matchedInvoiceIds: [best.invoiceId || ''], flags, flagMessages,
        };
      }
    }

    return { ...txn, flags, flagMessages };
  });
}
